<script setup lang="ts">
/**
 * The three steps before an assistant may be used (ADR 0066): the data
 * owners confirm, the access tests pass, the AI Board accepts. Shown the
 * same way in the assistant editor, My sign-offs and the review round, so
 * everyone involved reads the same three lines.
 */
import type { Readiness } from "../api/admin";

defineProps<{ readiness: Readiness; compact?: boolean }>();

const MARK = { ok: "✓", na: "–", todo: "" } as const;
</script>

<template>
  <ol :class="['steps', compact && 'steps--compact']">
    <li
      v-for="(s, i) in readiness.steps"
      :key="s.key"
      :class="['step', `step--${s.status}`]"
    >
      <span class="step__mark" aria-hidden="true">{{
        MARK[s.status] || i + 1
      }}</span>
      <span class="step__body">
        <span class="step__label"
          >{{ s.label
          }}<span v-if="s.status === 'na'" class="step__na">
            — not needed</span
          ></span
        >
        <span v-if="s.detail && !(compact && s.status !== 'todo')" class="step__detail">
          {{ s.detail }}
        </span>
      </span>
    </li>
  </ol>
</template>

<style scoped>
.steps {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.steps--compact {
  gap: 0.3rem;
}
.step {
  display: flex;
  gap: 0.6rem;
  align-items: flex-start;
}
.step__mark {
  flex: none;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: 700;
  border: 1.5px solid var(--color-border);
  color: var(--color-muted);
}
.step--ok .step__mark {
  border-color: var(--color-success);
  background: var(--color-success);
  color: var(--color-surface);
}
.step--na .step__mark {
  border-style: dashed;
}
.step--todo .step__mark {
  border-color: var(--color-warn-ink);
  color: var(--color-warn-ink);
}
.step__body {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
  padding-top: 0.1rem;
}
.step__label {
  font-weight: 600;
  color: var(--color-ink);
  font-size: 0.9rem;
}
.step--na .step__label {
  color: var(--color-muted);
  font-weight: 500;
}
.step__na {
  font-weight: 400;
}
.step__detail {
  font-size: 0.82rem;
  color: var(--color-muted);
  line-height: 1.4;
}
</style>
